import { hasValue } from "./data";
import { F } from "./gerencialFields";
import { toDate } from "./status";
import { precisaAcabamento } from "./toc/etapas";

export const ACABAMENTO_ITEMS = [
  {
    id: "pintura",
    field: F.PINTURA,
    label: "Pintura",
  },
  {
    id: "verniz",
    field: F.VERNIZ_ACAB,
    label: "Verniz",
  },
  {
    id: "pintura-perfil",
    field: F.PINTURA_PERFIL,
    label: "Pintura de Perfil",
  },
  {
    id: "pintura-serralheria",
    field: F.PINTURA_SERRALHERIA,
    label: "Pintura de Serralheria",
  },
];

function isItemMarcadoX(value) {
  if (!hasValue(value)) return false;
  return String(value).trim().toUpperCase() === "X";
}

// Inteiro estritamente positivo. "0" e qualquer outro valor não-numérico
// retornam false — porque "0" significa "nenhum item desse tipo a fazer",
// não "1 item pendente".
function isPositiveInteger(value) {
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return false;
  return Number(text) > 0;
}

// Regras (em ordem):
// - vazio                                  → NÃO precisa
// - "X"/"x"                                → NÃO precisa
// - "0"                                    → NÃO precisa (zero itens)
// - inteiro positivo (ex.: "3")            → precisa fazer (qty pendente)
// - data válida (apontamento de conclusão) → NÃO precisa (já feito)
// - texto descritivo livre                 → precisa fazer
export function ambientePrecisaItem(amb, item) {
  const value = amb?.[item.field];
  if (!hasValue(value)) return false;
  if (isItemMarcadoX(value)) return false;
  // "0" cai aqui: é inteiro mas não positivo.
  if (/^\d+$/.test(String(value).trim())) {
    return isPositiveInteger(value);
  }
  if (toDate(value)) return false;
  return true;
}

export function descricaoItemPendente(amb, item) {
  const valor = amb?.[item.field];
  if (!hasValue(valor)) return "";
  const texto = String(valor).trim();
  // "0" não é uma descrição útil — é só um marcador de "nada a fazer"
  // que já foi filtrado por ambientePrecisaItem; defensivamente aqui.
  if (texto === "0") return "";
  return texto;
}

// Definição direta — não depende de getEtapaAtual / fluxo TOC. Um ambiente
// está em acabamento se:
//   1. ACABAMENTO REALIZADO (CE) ainda vazio — só a data de recebimento/
//      conclusão do acabamento encerra a pendência.
//   2. Precisa de acabamento (ao menos uma das CA-CD ≠ "X"/"x").
//   3. Tem ao menos um item AINDA pendente (número inteiro em alguma CA-CD).
// REF-11: a data de CARREGAMENTO (saída para entrega) NÃO remove o item da
// fila de acabamento. Um ambiente pode ser carregado antes de o acabamento
// ser apontado como concluído; só ACABAMENTO_REALIZADO o tira da fila —
// alinhado a listarItensPendentesAcabamento, que sempre usou apenas esse campo.
// Usar getEtapaAtual aqui escondia ambientes em casos comuns onde alguma data
// upstream da planilha (USINAGEM, RECEBIMENTO TERCEIROS, MARCENARIA TÉRMINO)
// estava em branco, mesmo o ambiente fisicamente já estando em pintura.
export function isAmbienteEmAcabamento(amb) {
  if (toDate(amb?.[F.ACABAMENTO_REALIZADO])) return false;
  if (!precisaAcabamento(amb)) return false;
  return ACABAMENTO_ITEMS.some((item) => ambientePrecisaItem(amb, item));
}

// "Entrou em acabamento" = quando o ambiente fisicamente passou da
// marcenaria para a pintura. Em ordem de preferência:
//   1. MARCENARIA_TERMINO (BW) — apontamento real do operador.
//   2. FINAL_MARCENARIA_REPROGRAMADO (BZ) — quando há replanejamento
//      explícito anotado na planilha, é o melhor proxy disponível.
//   3. FINAL_MARCENARIA (BX) — fórmula WORKDAY; menos confiável que (1)
//      e (2), mas usável como referência aproximada.
//   4. MARCENARIA_INICIO (BV) — último recurso: pelo menos sabemos quando
//      a marcenaria começou; "parado" será conservador (mais alto).
export function dataEntradaAcabamento(amb) {
  return (
    toDate(amb?.[F.MARCENARIA_TERMINO]) ||
    toDate(amb?.[F.FINAL_MARCENARIA_REPROGRAMADO]) ||
    toDate(amb?.[F.FINAL_MARCENARIA]) ||
    toDate(amb?.[F.MARCENARIA_INICIO])
  );
}

// "Prazo do acabamento" = data alvo para terminar a pintura. Em ordem:
//   1. FINAL_ACABAMENTO_REPROGRAMADO (CG) — alvo interno fórmula
//      derivada da aprovação; é o prazo de produção.
//   2. ENTREGA_PREVISTA (N) — fallback ao prazo do contrato
//      com o cliente. Se acabamento atrasar até essa data, a entrega
//      atrasa.
export function prazoAcabamento(amb) {
  return (
    toDate(amb?.[F.FINAL_ACABAMENTO_REPROGRAMADO]) ||
    toDate(amb?.[F.ENTREGA_PREVISTA])
  );
}

export function listarItensPendentesAcabamento(amb) {
  // CE = ACABAMENTO REALIZADO. Quando preenchido, todos os itens que
  // precisavam estão considerados encerrados.
  const realizado = toDate(amb?.[F.ACABAMENTO_REALIZADO]);
  if (realizado) return [];
  return ACABAMENTO_ITEMS.filter((item) => ambientePrecisaItem(amb, item)).map(
    (item) => ({
      ...item,
      descricao: descricaoItemPendente(amb, item),
    })
  );
}
